import { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Form,
  Link,
  Paragraph,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Stack,
  Text,
  TextField,
  TextLabel,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { useLoginFlow } from '../login-flow';

const EMPTY_PASSWORD_ERROR = 'Enter your password.';

export function PasswordStep() {
  const { email, password, isSubmitting, setPassword, submitPassword, resetToEmailStep } = useLoginFlow();
  const [error, setError] = useState('');

  // Mirrors the Ember password controller's client-side validation: a blank
  // password shows an inline error and blocks submit (rather than round-tripping
  // an empty password to the server).
  const validate = (value: string): boolean => {
    const message = isEmptyString(value) ? EMPTY_PASSWORD_ERROR : '';
    setError(message);
    return message === '';
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate(password)) {
      return;
    }
    return submitPassword();
  };

  return (
    <>
      <PanelHeader>
        <Stack>
          <PanelTitle level="1" mb="0" data-test-login-signup-title>
            Welcome back,
            <Paragraph size="lg" weight="bold" my="050">
              <Text data-test-login-form-email-title>{email}</Text>!
            </Paragraph>
          </PanelTitle>
          <Paragraph>
            Not this account?{' '}
            <Link isUnderlined onClick={resetToEmailStep} data-test-login-form-use-different-email>
              Use a different email
            </Link>
          </Paragraph>
        </Stack>
      </PanelHeader>
      <PanelSection>
        <Form id="login-password-form" onSubmit={onSubmit}>
          <Box mb="200">
            <Flex justifyContent="space-between" alignItems="center">
              <TextLabel id="password-label" data-test-login-form-password-label>
                Password
              </TextLabel>
              {/* Reset password stays Ember-owned for now — cross-app handoff. */}
              <Link variant="secondary" href="/reset_password" data-test-login-form-forgot-password-link>
                Forgot password?
              </Link>
            </Flex>
            <TextField
              id="password"
              ariaLabelledby="password-label"
              data-testid="login-password"
              data-test-login-form-input="password"
              name="password"
              type="password"
              autoComplete="password"
              value={password}
              error={error}
              onInput={(event) => {
                setPassword(event.target.value);
                validate(event.target.value);
              }}
              autoFocus
              shouldAllowAutofill
            />
          </Box>

          <Box mt="300">
            <Button
              data-test-login-form-submit
              type="submit"
              variant="primary"
              icon="chevron-right"
              iconPosition="trailing"
              w="full"
              isDisabled={!isEmptyString(error)}
              isLoading={isSubmitting}
            >
              Log in to Customer.io
            </Button>
          </Box>
        </Form>
      </PanelSection>
    </>
  );
}
